import React, { useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Paper,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import { toast } from 'react-toastify';
import advertisementService from '../../../../../services/advertisementService';

// Извлекает YouTube video ID из любого формата ссылки
function extractYoutubeId(url) {
	if (!url) return null;
	const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]{11})/);
	return m ? m[1] : null;
}

function AdManagement() {
	const [videos, setVideos] = useState([]);
	const [settings, setSettings] = useState({ adsEnabled: false });
	const [loading, setLoading] = useState(true);
	const [savingSettings, setSavingSettings] = useState(false);

	// Диалог добавления/редактирования
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [formUrl, setFormUrl] = useState('');
	const [formActive, setFormActive] = useState(true);
	const [formError, setFormError] = useState('');
	const [formSaving, setFormSaving] = useState(false);

	// Диалог подтверждения удаления
	const [deleteId, setDeleteId] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [vRes, sRes] = await Promise.all([
				advertisementService.getAllForAdmin(),
				advertisementService.getSettings(),
			]);
			setVideos(vRes.data || []);
			setSettings(sRes.data || { adsEnabled: false });
		} catch {
			toast.error('Не удалось загрузить данные');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { load(); }, [load]);

	// ── Сохранение настроек ────────────────────────────────────────────────────
	const saveSettings = async (patch) => {
		setSavingSettings(true);
		const next = { ...settings, ...patch };
		try {
			const res = await advertisementService.updateSettings(next);
			setSettings(res.data);
			toast.success('Настройки сохранены');
		} catch {
			toast.error('Ошибка сохранения настроек');
		} finally {
			setSavingSettings(false);
		}
	};

	// ── Открытие диалога ───────────────────────────────────────────────────────
	const openAdd = () => {
		setEditing(null);
		setFormUrl('');
		setFormActive(true);
		setFormError('');
		setDialogOpen(true);
	};

	const openEdit = (video) => {
		setEditing(video);
		setFormUrl(video.youtubeUrl);
		setFormActive(video.isActive);
		setFormError('');
		setDialogOpen(true);
	};

	// ── Сохранение ролика ──────────────────────────────────────────────────────
	const saveVideo = async () => {
		if (!formUrl.trim()) { setFormError('Укажите ссылку на YouTube'); return; }
		if (!extractYoutubeId(formUrl)) { setFormError('Неверный формат ссылки YouTube'); return; }

		setFormSaving(true);
		setFormError('');
		try {
			const payload = {
				youtubeUrl: formUrl.trim(),
				orderIndex: editing ? editing.orderIndex : videos.length,
				isActive: formActive,
			};
			if (editing) {
				await advertisementService.updateVideo(editing.id, payload);
				toast.success('Ролик обновлён');
			} else {
				await advertisementService.createVideo(payload);
				toast.success('Ролик добавлен');
			}
			setDialogOpen(false);
			load();
		} catch (e) {
			setFormError(e?.response?.data?.message || 'Ошибка сохранения');
		} finally {
			setFormSaving(false);
		}
	};

	// ── Удаление ───────────────────────────────────────────────────────────────
	const confirmDelete = async () => {
		try {
			await advertisementService.deleteVideo(deleteId);
			toast.success('Ролик удалён');
			setDeleteId(null);
			load();
		} catch {
			toast.error('Ошибка удаления');
		}
	};

	// ── Смена порядка ──────────────────────────────────────────────────────────
	const moveVideo = async (idx, direction) => {
		const target = idx + direction;
		if (target < 0 || target >= videos.length) return;
		const list = [...videos];
		[list[idx], list[target]] = [list[target], list[idx]];
		try {
			await Promise.all([
				advertisementService.updateVideo(list[idx].id, {
					youtubeUrl: list[idx].youtubeUrl,
					orderIndex: idx,
					isActive: list[idx].isActive,
				}),
				advertisementService.updateVideo(list[target].id, {
					youtubeUrl: list[target].youtubeUrl,
					orderIndex: target,
					isActive: list[target].isActive,
				}),
			]);
			load();
		} catch {
			toast.error('Ошибка изменения порядка');
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ maxWidth: 900, mx: 'auto' }}>
			{/* ── Блок настроек ─────────────────────────────────────────── */}
			<Paper
				variant='outlined'
				sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)' }}
			>
				<Typography variant='h6' sx={{ fontWeight: 700, mb: 2 }}>
					Настройки отображения
				</Typography>

				<Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={2}>
					<Box>
						<Typography sx={{ fontWeight: 600 }}>Показывать рекламные ролики</Typography>
						<Typography variant='body2' color='text.secondary'>
							Включите, чтобы ролики отображались на главном экране вместо часов
						</Typography>
					</Box>
					<FormControlLabel
						control={
							<Switch
								checked={settings.adsEnabled}
								onChange={(e) => saveSettings({ adsEnabled: e.target.checked })}
								disabled={savingSettings}
								color='primary'
							/>
						}
						label={
							<Chip
								size='small'
								label={settings.adsEnabled ? 'Включено' : 'Выключено'}
								color={settings.adsEnabled ? 'success' : 'default'}
								sx={{ fontWeight: 700 }}
							/>
						}
					/>
				</Stack>
			</Paper>

			{/* ── Список роликов ────────────────────────────────────────── */}
			<Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1.5 }}>
				<Typography variant='h6' sx={{ fontWeight: 700 }}>
					Ролики ({videos.length})
				</Typography>
				<Button
					variant='contained'
					startIcon={<AddRoundedIcon />}
					onClick={openAdd}
					sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
				>
					Добавить ролик
				</Button>
			</Stack>

			{videos.length === 0 ? (
				<Paper
					variant='outlined'
					sx={{ p: 4, borderRadius: 3, textAlign: 'center', border: '1px solid rgba(0,0,0,0.08)' }}
				>
					<PlayCircleOutlineRoundedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
					<Typography color='text.secondary'>Нет добавленных роликов</Typography>
					<Typography variant='body2' color='text.disabled' sx={{ mt: 0.5 }}>
						Нажмите «Добавить ролик» чтобы вставить ссылку на YouTube
					</Typography>
				</Paper>
			) : (
				<TableContainer
					component={Paper}
					variant='outlined'
					sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)' }}
				>
					<Table size='small'>
						<TableHead>
							<TableRow sx={{ backgroundColor: 'rgba(25,118,210,0.06)' }}>
								<TableCell sx={{ fontWeight: 700, width: 60 }}>№</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>Ролик</TableCell>
								<TableCell sx={{ fontWeight: 700, width: 110 }} align='center'>Статус</TableCell>
								<TableCell sx={{ fontWeight: 700, width: 160 }} align='center'>Порядок</TableCell>
								<TableCell sx={{ fontWeight: 700, width: 100 }} align='center'>Действия</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{videos.map((v, idx) => {
								const ytId = extractYoutubeId(v.youtubeUrl);
								const thumb = ytId
									? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`
									: null;
								return (
									<TableRow key={v.id} hover>
										<TableCell>{idx + 1}</TableCell>
										<TableCell>
											<Stack direction='row' alignItems='center' spacing={1.5}>
												{thumb && (
													<Box
														component='img'
														src={thumb}
														alt=''
														sx={{ width: 80, height: 45, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
													/>
												)}
												<Box sx={{ minWidth: 0 }}>
													<Typography
														variant='body2'
														sx={{
															fontWeight: 600,
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap',
															maxWidth: 320
														}}
													>
														{v.youtubeUrl}
													</Typography>
													{ytId && (
														<Typography variant='caption' color='text.secondary'>
															ID: {ytId}
														</Typography>
													)}
												</Box>
											</Stack>
										</TableCell>
										<TableCell align='center'>
											<Chip
												size='small'
												label={v.isActive ? 'Активен' : 'Скрыт'}
												color={v.isActive ? 'success' : 'default'}
												sx={{ fontWeight: 600 }}
											/>
										</TableCell>
										<TableCell align='center'>
											<Stack direction='row' justifyContent='center' spacing={0.5}>
												<Tooltip title='Вверх'>
													<span>
														<IconButton size='small' onClick={() => moveVideo(idx, -1)} disabled={idx === 0}>
															<ArrowUpwardRoundedIcon fontSize='small' />
														</IconButton>
													</span>
												</Tooltip>
												<Tooltip title='Вниз'>
													<span>
														<IconButton size='small' onClick={() => moveVideo(idx, 1)} disabled={idx === videos.length - 1}>
															<ArrowDownwardRoundedIcon fontSize='small' />
														</IconButton>
													</span>
												</Tooltip>
											</Stack>
										</TableCell>
										<TableCell align='center'>
											<Stack direction='row' justifyContent='center' spacing={0.5}>
												<Tooltip title='Редактировать'>
													<IconButton size='small' color='primary' onClick={() => openEdit(v)}>
														<EditRoundedIcon fontSize='small' />
													</IconButton>
												</Tooltip>
												<Tooltip title='Удалить'>
													<IconButton size='small' color='error' onClick={() => setDeleteId(v.id)}>
														<DeleteRoundedIcon fontSize='small' />
													</IconButton>
												</Tooltip>
											</Stack>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{/* ── Диалог добавления/редактирования ─────────────────────── */}
			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
				<DialogTitle sx={{ fontWeight: 700 }}>
					{editing ? 'Редактировать ролик' : 'Добавить ролик'}
				</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						{formError && <Alert severity='error'>{formError}</Alert>}
						<TextField
							label='Ссылка на YouTube'
							placeholder='https://www.youtube.com/watch?v=...'
							value={formUrl}
							onChange={(e) => setFormUrl(e.target.value)}
							fullWidth
							autoFocus
							helperText='Поддерживаются форматы youtube.com/watch?v= и youtu.be/'
						/>
						{extractYoutubeId(formUrl) && (
							<Box
								component='img'
								src={`https://img.youtube.com/vi/${extractYoutubeId(formUrl)}/mqdefault.jpg`}
								alt='preview'
								sx={{ width: '100%', borderRadius: 2, maxHeight: 200, objectFit: 'cover' }}
							/>
						)}
						<FormControlLabel
							control={
								<Switch
									checked={formActive}
									onChange={(e) => setFormActive(e.target.checked)}
									color='primary'
								/>
							}
							label='Активный (отображается на главном экране)'
						/>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					<Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
						Отмена
					</Button>
					<Button
						variant='contained'
						onClick={saveVideo}
						disabled={formSaving}
						sx={{ textTransform: 'none', fontWeight: 600 }}
					>
						{formSaving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
						{editing ? 'Сохранить' : 'Добавить'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* ── Диалог подтверждения удаления ────────────────────────── */}
			<Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
				<DialogTitle sx={{ fontWeight: 700 }}>Удалить ролик?</DialogTitle>
				<DialogContent>
					<Typography>Это действие нельзя отменить.</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 2, pb: 2 }}>
					<Button onClick={() => setDeleteId(null)} sx={{ textTransform: 'none' }}>Отмена</Button>
					<Button variant='contained' color='error' onClick={confirmDelete} sx={{ textTransform: 'none', fontWeight: 600 }}>
						Удалить
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default AdManagement;
